/**
 * The drawing editor's state (gallery F-6): tools, the draft shapes, undo/redo, selection and the
 * in-progress stroke. A pure reducer — every change returns new objects, nothing is mutated — so
 * each behaviour is testable without a browser.
 *
 * The editor enforces the server's caps (MARKUP_LIMITS) before a mark is added, so a save is never
 * refused for size: a mark that would exceed them is dropped with `notice` set.
 */
import {
  MARKUP_LIMITS,
  type MarkupColor,
  type MarkupPoint,
  type MarkupShape,
  type MarkupTextSize,
  type MarkupWidth,
} from '../../../../types/fieldService/generatorPhotoMarkupVocab';
import { clamp01, simplifyStroke, totalPoints, translateShape } from './markupGeometry';

export type MarkupTool = 'select' | 'arrow' | 'ellipse' | 'freehand' | 'text' | 'textBox';

export interface MarkupEditorState {
  tool: MarkupTool;
  color: MarkupColor;
  width: MarkupWidth;
  textSize: MarkupTextSize;
  /** What the text and text-box tools will write. */
  text: string;
  shapes: MarkupShape[];
  past: MarkupShape[][];
  future: MarkupShape[][];
  selectedId: string | null;
  /** The mark being drawn right now (shown, not yet in `shapes`). */
  draft: MarkupShape | null;
  /** Drag bookkeeping for draw and move. */
  anchor: MarkupPoint | null;
  moveOrigin: MarkupShape[] | null;
  /**
   * The revision last picked from "Earlier versions", and the exact shapes it restored. It only
   * counts while the draft still IS those shapes (see restoredFromOf): undo past it, or change a
   * mark, and the save is an ordinary save again, never a false "restored from k".
   */
  restoredFrom: number | null;
  restoredShapes: MarkupShape[] | null;
  /** A short message for the user (e.g. "Type the text first"). */
  notice: string | null;
  /** The shapes the editor opened with; `dirty` compares against it. */
  base: MarkupShape[];
  /**
   * The saved revision `base` came from. A save names THIS revision, never whatever the live query
   * holds at save time: a background refetch mid-edit must not let this save overwrite marks it
   * never saw (the server refuses a stale revision as `aborted`).
   */
  baseRevision: number;
  seq: number;
}

export type MarkupAction =
  | { type: 'setTool'; tool: MarkupTool }
  | { type: 'setColor'; color: MarkupColor }
  | { type: 'setWidth'; width: MarkupWidth }
  | { type: 'setTextSize'; size: MarkupTextSize }
  | { type: 'setText'; text: string }
  | { type: 'pointerDown'; point: MarkupPoint }
  | { type: 'pointerMove'; point: MarkupPoint }
  | { type: 'pointerUp' }
  | { type: 'pickShape'; id: string; point: MarkupPoint }
  | { type: 'deleteSelected' }
  | { type: 'undo' }
  | { type: 'redo' }
  | { type: 'clearAll' }
  | { type: 'restore'; shapes: MarkupShape[]; revision: number }
  | { type: 'dismissNotice' }
  /** Start a fresh editing session from the saved drawing (keeps the chosen tool, colour and sizes). */
  | { type: 'reset'; shapes: MarkupShape[]; revision: number }
  /**
   * Someone else saved while this editor was open: lay this editor's own changes (by shape id) over
   * their newer drawing and carry on from their revision. See rebaseShapes.
   */
  | { type: 'rebase'; shapes: MarkupShape[]; revision: number };

const TEXT_TOOLS: MarkupTool[] = ['text', 'textBox'];
const MIN_DRAG = 0.01;

export function initialEditorState(shapes: MarkupShape[], revision = 0): MarkupEditorState {
  return {
    tool: 'arrow',
    color: 'red',
    width: 'medium',
    textSize: 'medium',
    text: '',
    shapes,
    past: [],
    future: [],
    selectedId: null,
    draft: null,
    anchor: null,
    moveOrigin: null,
    restoredFrom: null,
    restoredShapes: null,
    notice: null,
    base: shapes,
    baseRevision: revision,
    seq: 0,
  };
}

const same = (a: MarkupShape[] | null, b: MarkupShape[] | null) => JSON.stringify(a) === JSON.stringify(b);

/** The revision this draft restores, or null once the draft has moved away from it. */
export function restoredFromOf(state: MarkupEditorState): number | null {
  return state.restoredFrom !== null && same(state.shapes, state.restoredShapes) ? state.restoredFrom : null;
}

export function isDirty(state: MarkupEditorState): boolean {
  return restoredFromOf(state) !== null || !same(state.shapes, state.base);
}

export const REBASE_NOTICE = 'Someone else saved this drawing while you were working. Your changes are laid over theirs: check it, then save.';

/**
 * Three-way merge by shape id. `mine` was drawn on `base`; `theirs` is the newer saved drawing.
 * Marks I removed stay removed, marks I added or changed are mine, everything else is theirs.
 * A mark they removed that I changed is kept (my change is the newer intent for it).
 */
export function rebaseShapes(base: MarkupShape[], mine: MarkupShape[], theirs: MarkupShape[]): MarkupShape[] {
  const baseById = new Map(base.map((s) => [s.id, s]));
  const mineById = new Map(mine.map((s) => [s.id, s]));
  const removed = new Set(base.filter((s) => !mineById.has(s.id)).map((s) => s.id));
  const changed = new Map(
    mine.filter((s) => baseById.has(s.id) && !same([s], [baseById.get(s.id)!])).map((s) => [s.id, s])
  );
  const kept = theirs.filter((s) => !removed.has(s.id)).map((s) => changed.get(s.id) ?? s);
  const keptIds = new Set(kept.map((s) => s.id));
  const revived = Array.from(changed.values()).filter((s) => !keptIds.has(s.id));
  const added = mine.filter((s) => !baseById.has(s.id) && !keptIds.has(s.id));
  return [...kept, ...revived, ...added];
}

function nextId(state: MarkupEditorState): string {
  return `s${Date.now().toString(36)}${state.seq.toString(36)}`;
}

/** Push a new shapes list onto the undo history. */
function commit(state: MarkupEditorState, shapes: MarkupShape[], extra: Partial<MarkupEditorState> = {}): MarkupEditorState {
  return { ...state, past: [...state.past, state.shapes], future: [], shapes, draft: null, anchor: null, ...extra };
}

function withinCaps(state: MarkupEditorState, shape: MarkupShape): string | null {
  if (state.shapes.length >= MARKUP_LIMITS.maxShapes) return `This drawing already has ${MARKUP_LIMITS.maxShapes} marks, the most one photo can hold.`;
  const pts = shape.type === 'freehand' ? shape.points.length : 0;
  if (totalPoints(state.shapes) + pts > MARKUP_LIMITS.maxTotalPoints) return 'This drawing is full — remove a squiggle or two to add more.';
  return null;
}

function addShape(state: MarkupEditorState, shape: MarkupShape): MarkupEditorState {
  const blocked = withinCaps(state, shape);
  if (blocked) return { ...state, draft: null, anchor: null, notice: blocked };
  return commit({ ...state, seq: state.seq + 1 }, [...state.shapes, shape], { notice: null });
}

function base(state: MarkupEditorState) {
  return { id: nextId(state), color: state.color, width: state.width };
}

/** The mark being dragged out from `anchor` to `point`, for the current tool. */
function draftFor(state: MarkupEditorState, point: MarkupPoint): MarkupShape | null {
  const a = state.anchor;
  if (!a) return null;
  switch (state.tool) {
    case 'arrow':
      return { ...base(state), type: 'arrow', from: a, to: point };
    case 'ellipse':
      return {
        ...base(state),
        type: 'ellipse',
        cx: (a.x + point.x) / 2,
        cy: (a.y + point.y) / 2,
        rx: Math.abs(point.x - a.x) / 2,
        ry: Math.abs(point.y - a.y) / 2,
      };
    case 'freehand': {
      const prev = state.draft?.type === 'freehand' ? state.draft.points : [a];
      return { ...base(state), type: 'freehand', points: [...prev, point] };
    }
    case 'textBox':
      return {
        ...base(state),
        type: 'textBox',
        x: Math.min(a.x, point.x),
        y: Math.min(a.y, point.y),
        w: Math.abs(point.x - a.x),
        h: Math.abs(point.y - a.y),
        text: state.text.trim() || 'Text',
        size: state.textSize,
      };
    default:
      return null;
  }
}

/** Turn the finished drag into a stored mark, or null when it was too small to mean anything. */
function finalise(state: MarkupEditorState): MarkupShape | null {
  const d = state.draft;
  if (!d) return null;
  switch (d.type) {
    case 'arrow':
      return Math.hypot(d.to.x - d.from.x, d.to.y - d.from.y) >= MIN_DRAG ? d : null;
    case 'ellipse':
      return d.rx >= MIN_DRAG / 2 && d.ry >= MIN_DRAG / 2 ? d : null;
    case 'freehand':
      return d.points.length >= 2 ? { ...d, points: simplifyStroke(d.points) } : null;
    case 'textBox':
      return d.w >= 2 * MIN_DRAG && d.h >= 2 * MIN_DRAG ? { ...d, text: state.text.trim() } : null;
    default:
      return null;
  }
}

function pointerDown(state: MarkupEditorState, point: MarkupPoint): MarkupEditorState {
  if (state.tool === 'select') return { ...state, selectedId: null };
  if (TEXT_TOOLS.includes(state.tool) && !state.text.trim()) {
    return { ...state, notice: 'Type the text first, then tap the photo.' };
  }
  if (state.tool === 'text') {
    const at = { x: clamp01(point.x), y: clamp01(point.y) };
    return addShape(state, { ...base(state), type: 'text', at, text: state.text.trim(), size: state.textSize });
  }
  return { ...state, anchor: point, draft: null, selectedId: null, notice: null };
}

function pointerMove(state: MarkupEditorState, point: MarkupPoint): MarkupEditorState {
  if (state.moveOrigin && state.selectedId && state.anchor) {
    const dx = point.x - state.anchor.x;
    const dy = point.y - state.anchor.y;
    const shapes = state.moveOrigin.map((s) => (s.id === state.selectedId ? translateShape(s, dx, dy) : s));
    return { ...state, shapes };
  }
  if (!state.anchor) return state;
  return { ...state, draft: draftFor(state, point) };
}

function pointerUp(state: MarkupEditorState): MarkupEditorState {
  if (state.moveOrigin) {
    const moved = JSON.stringify(state.moveOrigin) !== JSON.stringify(state.shapes);
    const settled = { ...state, moveOrigin: null, anchor: null };
    return moved ? { ...settled, past: [...state.past, state.moveOrigin], future: [] } : settled;
  }
  const shape = finalise(state);
  if (!shape) return { ...state, draft: null, anchor: null };
  return addShape(state, shape);
}

export function markupEditorReducer(state: MarkupEditorState, action: MarkupAction): MarkupEditorState {
  switch (action.type) {
    case 'setTool':
      return { ...state, tool: action.tool, selectedId: null, draft: null, anchor: null, notice: null };
    case 'setColor': {
      // Recolouring the selected mark is a change of its own (undoable).
      if (state.selectedId) {
        const shapes = state.shapes.map((s) => (s.id === state.selectedId ? { ...s, color: action.color } : s));
        return commit({ ...state, color: action.color }, shapes, { selectedId: state.selectedId });
      }
      return { ...state, color: action.color };
    }
    case 'setWidth':
      return { ...state, width: action.width };
    case 'setTextSize':
      return { ...state, textSize: action.size };
    case 'setText':
      return { ...state, text: action.text.slice(0, MARKUP_LIMITS.maxTextLength), notice: null };
    case 'pointerDown':
      return pointerDown(state, action.point);
    case 'pointerMove':
      return pointerMove(state, action.point);
    case 'pointerUp':
      return pointerUp(state);
    case 'pickShape':
      if (state.tool !== 'select') return state;
      return { ...state, selectedId: action.id, anchor: action.point, moveOrigin: state.shapes, notice: null };
    case 'deleteSelected':
      if (!state.selectedId) return state;
      return commit(state, state.shapes.filter((s) => s.id !== state.selectedId), { selectedId: null });
    case 'undo': {
      if (state.past.length === 0) return state;
      const previous = state.past[state.past.length - 1];
      return { ...state, shapes: previous, past: state.past.slice(0, -1), future: [state.shapes, ...state.future], selectedId: null };
    }
    case 'redo': {
      if (state.future.length === 0) return state;
      const [next, ...rest] = state.future;
      return { ...state, shapes: next, past: [...state.past, state.shapes], future: rest, selectedId: null };
    }
    case 'clearAll':
      if (state.shapes.length === 0) return state;
      return commit(state, [], { selectedId: null });
    case 'restore':
      return commit(state, action.shapes, { selectedId: null, restoredFrom: action.revision, restoredShapes: action.shapes });
    case 'dismissNotice':
      return { ...state, notice: null };
    case 'rebase': {
      const merged = rebaseShapes(state.base, state.shapes, action.shapes);
      return {
        ...state,
        shapes: merged,
        base: action.shapes,
        baseRevision: action.revision,
        past: [],
        future: [],
        selectedId: null,
        draft: null,
        anchor: null,
        moveOrigin: null,
        restoredFrom: null,
        restoredShapes: null,
        notice: REBASE_NOTICE,
      };
    }
    case 'reset':
      return {
        ...initialEditorState(action.shapes, action.revision),
        tool: state.tool === 'select' ? 'arrow' : state.tool,
        color: state.color,
        width: state.width,
        textSize: state.textSize,
      };
  }
}
